'use client';

import { AppImageClient, AppImageData } from '@components/appImage';
import { CTAButtons } from '@components/ctaButtons';
import { Sanitizer } from '@components/sanitizer';
import { Typography } from '@components/typography';
import { classNames } from '@lib/classNames/classNames';

import { ServiceCommonCard, ServiceCTACard } from './types';

import { Card } from '@/shared/components/card';
import { isCtaCard } from '@/shared/types/card';

export type ServiceCard = ServiceCTACard | ServiceCommonCard;

interface ServiceOverviewProps {
  cards: ServiceCard[];
  /** Готовые signed URL по imgSrcKey. Legacy — для новых страниц используйте imagesDataMap. */
  imagesMap?: Record<string, string>;
  /** Данные AppImage по imgSrcKey / imgSrcKeySm из `getAppImagesDataWithSizes`. Приоритетнее imagesMap. */
  imagesDataMap?: Record<string, AppImageData>;
  desktopCardHeight?: string | number;
  gridCols: number;
}

export const ServiceOverview = ({
  cards,
  imagesMap,
  imagesDataMap,
  desktopCardHeight,
  gridCols,
}: ServiceOverviewProps) => {
  return (
    <>
      {/* ----------- Мобильная версия ----------- */}
      <div className="flex flex-col gap-4 px-4 md:hidden">
        {cards.map((card) => {
          const imageKey = card.imgSrcKeySm ?? card.imgSrcKey;
          const imageData = imagesDataMap?.[imageKey];
          const image = imagesMap?.[imageKey];

          if (isCtaCard(card)) {
            return (
              <Card overflowHidden key={card.id}>
                <div
                  className="bg-surface-base-inverse-primary relative flex size-full flex-col justify-between gap-6"
                  style={{
                    paddingTop: card.paddingConfigMobile?.top ?? 20,
                    paddingRight: card.paddingConfigMobile?.right ?? 20,
                    paddingBottom: card.paddingConfigMobile?.bottom ?? 20,
                    paddingLeft: card.paddingConfigMobile?.left ?? 20,
                    height: card.cardHeight,
                  }}
                >
                  {card.title && (
                    <Typography variant="heading-m">{card.title}</Typography>
                  )}

                  {(card.mainButton?.label || card.secondaryButton?.label) && (
                    <div className="z-1 flex">
                      <div className="flex w-full flex-col-reverse gap-2">
                        <CTAButtons
                          mainButton={card.mainButton}
                          secondaryButton={card.secondaryButton}
                        />
                      </div>
                    </div>
                  )}

                  {/* Декоративная графика */}
                  {imageData ? (
                    <AppImageClient
                      {...imageData}
                      alt={card.imgSrcKey}
                      className={classNames({
                        cls: 'pointer-events-none absolute size-auto object-cover',
                      })}
                      style={{
                        top: card.imgConfig.top,
                        right: card.imgConfig.right,
                        bottom: card.imgConfig.bottom,
                        left: card.imgConfig.left,
                        width: card.imgConfig.width ?? 'auto',
                        height: card.imgConfig.height ?? 'auto',
                      }}
                    />
                  ) : (
                    image && (
                      <img
                        src={image}
                        alt={card.imgSrcKey}
                        className={classNames({
                          cls: 'pointer-events-none absolute size-auto object-cover',
                        })}
                        style={{
                          top: card.imgConfig.top,
                          right: card.imgConfig.right,
                          bottom: card.imgConfig.bottom,
                          left: card.imgConfig.left,
                          width: card.imgConfig.width ?? 'auto',
                          height: card.imgConfig.height ?? 'auto',
                        }}
                      />
                    )
                  )}
                </div>
              </Card>
            );
          }

          return (
            <Card key={card.id}>
              <div
                className="flex size-full flex-col justify-between gap-6"
                style={{
                  paddingTop: card.paddingConfigMobile?.top ?? 20,
                  paddingRight: card.paddingConfigMobile?.right ?? 20,
                  paddingBottom: card.paddingConfigMobile?.bottom ?? 0,
                  paddingLeft: card.paddingConfigMobile?.left ?? 20,
                }}
              >
                <div className="flex flex-col gap-3">
                  {card.title && (
                    <Typography variant="heading-s">{card.title}</Typography>
                  )}
                  <Typography variant="text-m" color="secondary">
                    <Sanitizer
                      as="span"
                      htmlContent={card.description}
                      className="[&_a]:text-text-base-primary text-[16px] leading-[24px] tracking-[-0.16px] [&_a]:underline"
                    />
                  </Typography>
                </div>
                {/* Декоративная графика */}
                {imageData ? (
                  <AppImageClient
                    {...imageData}
                    alt={card.imgSrcKey}
                    className={classNames({
                      cls: 'pointer-events-none size-auto object-cover',
                    })}
                  />
                ) : (
                  image && (
                    <img
                      src={image}
                      alt={card.imgSrcKey}
                      className={classNames({
                        cls: 'pointer-events-none size-auto object-cover',
                      })}
                    />
                  )
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* ----------- Десктоп версия ----------- */}
      <div
        className="grid gap-8 px-4 max-sm:hidden md:grid-cols-2"
        style={{
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
        }}
      >
        {cards.map((card) => {
          if (isCtaCard(card)) {
            const imageData = imagesDataMap?.[card.imgSrcKey];
            const image = imagesMap?.[card.imgSrcKey];

            return (
              <div
                key={card.id}
                className="md:col-span-1 lg:col-span-2"
                style={{
                  height: card.cardHeight ?? desktopCardHeight,
                  gridColumn: `span ${card.colSpan}`,
                }}
              >
                <Card overflowHidden>
                  <div className="bg-surface-base-inverse-primary relative flex h-full flex-col justify-between px-8 py-6 max-md:p-5">
                    {/* Декоративная графика */}
                    {imageData ? (
                      <AppImageClient
                        {...imageData}
                        alt={card.imgSrcKey}
                        className={classNames({
                          cls: 'pointer-events-none absolute w-full object-cover min-[390px]:top-[50px] min-[430px]:top-[31px]',
                        })}
                        style={{
                          top: card.imgConfig.top,
                          right: card.imgConfig.right,
                          bottom: card.imgConfig.bottom,
                          left: card.imgConfig.left,
                          width: card.imgConfig.width ?? 'auto',
                          height: card.imgConfig.height ?? 'auto',
                        }}
                      />
                    ) : (
                      image && (
                        <img
                          src={image}
                          alt={card.imgSrcKey}
                          className={classNames({
                            cls: 'pointer-events-none absolute w-full object-cover min-[390px]:top-[50px] min-[430px]:top-[31px]',
                          })}
                          style={{
                            top: card.imgConfig.top,
                            right: card.imgConfig.right,
                            bottom: card.imgConfig.bottom,
                            left: card.imgConfig.left,
                            width: card.imgConfig.width ?? 'auto',
                            height: card.imgConfig.height ?? 'auto',
                          }}
                        />
                      )
                    )}

                    <div className="z-1 flex h-full flex-col justify-between">
                      <Typography variant="heading-m">{card.title}</Typography>
                      {(card.mainButton?.label ||
                        card.secondaryButton?.label) && (
                        <div className="flex">
                          <div className="flex flex-col-reverse gap-3">
                            <CTAButtons
                              mainButton={card.mainButton}
                              secondaryButton={card.secondaryButton}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            );
          }

          const imageData = imagesDataMap?.[card.imgSrcKey];
          const image = imagesMap?.[card.imgSrcKey];

          return (
            <Card
              key={card.id}
              overflowHidden
              style={{ gridColumn: `span ${card.colSpan}` }}
            >
              <div
                className="flex flex-col justify-between p-6 pb-0"
                style={{ height: card.cardHeight ?? desktopCardHeight }}
              >
                <div className="flex flex-col gap-3">
                  <Typography variant="heading-s">{card.title}</Typography>
                  <Typography variant="text-base" color="secondary">
                    <Sanitizer
                      as="span"
                      htmlContent={card.description}
                      className="[&_a]:text-text-base-primary text-[16px] leading-[24px] [&_a]:underline"
                    />
                  </Typography>
                </div>
                {imageData ? (
                  <AppImageClient
                    {...imageData}
                    style={{
                      paddingBottom: card.paddingConfigWeb?.bottom ?? 0,
                    }}
                    className="h-auto w-full"
                    alt={card.imgSrcKey}
                  />
                ) : (
                  <img
                    style={{
                      paddingBottom: card.paddingConfigWeb?.bottom ?? 0,
                    }}
                    src={image}
                    alt={card.imgSrcKey}
                  />
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
};
